// eslint-disable prettier/prettier
import React, { useEffect, useState, useRef } from 'react';
import { LoadingButton } from '@mui/lab';
import Switch from 'components/Switch';
import { getInvoiceAmounts } from 'services/clients/subscription';
import { formatCurrencyNumber, formatNumber } from 'utils/common';
import CouponInput from './components/CouponInput';
import LoadingComponent from 'components/LoadingComponent';
import _ from 'lodash';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/KeyboardArrowDown';
import { BUTTON_ACTIONS, CHECKOUT_STEPS } from './constants';

const AbortController = window.AbortController;

const CheckoutCard = ({
  user,
  selectedPack,
  selectedVolume,
  onCheckout,
  formData,
  checkoutIsLoading,
  onCoupon,
  period,
  step,
  onPeriodChange = () => {},
}) => {
  const availablePeriods =
    Object.keys(selectedPack.yearlyAvailableVolumes).length > 0
      ? [
          { value: 'monthly', label: 'Bill Monthly' },
          { value: 'yearly', label: 'Bill Yearly' },
        ]
      : [{ value: 'monthly', label: 'Bill Monthly' }];
  const [switchActiveItem, setSwitchActiveItem] = useState(period);
  const [invoiceAmounts, setInvoiceAmounts] = useState({});
  const [monthlyInvoiceAmounts, setMonthlyInvoiceAmounts] = useState(null);
  const [couponCode, setCouponCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  let abortController = useRef(AbortController ? new AbortController() : null);
  const isMonthly = period === 'monthly';
  const currentPack = _.get(user, 'company.pack');
  const periodSwitchAllowed =
    step === CHECKOUT_STEPS.BUILD_PLAN ||
    (Object.keys(selectedPack.yearlyAvailableVolumes).includes(selectedVolume.toString()) &&
      Object.keys(selectedPack.monthlyAvailableVolumes).includes(selectedVolume.toString()));

  if (currentPack === 'starter' && selectedPack.id === 'starter') {
    formData.isValid = false;
  } else if (step === CHECKOUT_STEPS.BUILD_PLAN) {
    formData.isValid = true;
  }

  const abortAndRecreate = () => {
    if (abortController.current && Object.keys(invoiceAmounts).length > 0) {
      abortController.current.abort();
      abortController.current = AbortController ? new AbortController() : null;
    }
  };

  const handleOnItemSelected = (item, idx, value = null) => {
    if (item) {
      setSwitchActiveItem(item.value);
      onPeriodChange(item.value);
    } else if (value) {
      setSwitchActiveItem(value);
      onPeriodChange(value);
    }
  };

  useEffect(() => {
    const doIt = async () => {
      let data = null;
      abortAndRecreate();
      try {
        setIsLoading(true);
        data = await getInvoiceAmounts(
          selectedPack.id,
          selectedVolume,
          period,
          couponCode,
          abortController.current && abortController.current.signal
        );
        if (period === 'yearly') {
          const monthlyData = await getInvoiceAmounts(
            selectedPack.id,
            selectedVolume,
            'monthly',
            couponCode,
            abortController.current && abortController.current.signal
          );
          setMonthlyInvoiceAmounts(monthlyData);
        } else {
          setMonthlyInvoiceAmounts(null);
        }
      } catch {
        setIsLoading(false);
        return;
      }
      if (couponCode) {
        onCoupon(couponCode);
      }
      setIsLoading(false);
      setInvoiceAmounts(data);
    };
    doIt();
  }, [selectedVolume, selectedPack, period, couponCode]);

  const isFreePlan = invoiceAmounts.amountDueNextMonth === 0 && invoiceAmounts.amountDueNow === 0;
  return (
    <>
      <div className="bg-white shadow-sm rounded-md p-4">
        <div className="flex flex-col space-y-8 items-start">
          {isFreePlan ? (
            <CheckoutCardFreePlan selectedPack={selectedPack} selectedVolume={selectedVolume} />
          ) : (
            <>
              <Switch
                items={availablePeriods}
                activeItem={switchActiveItem}
                onItemSelected={handleOnItemSelected}
                disabled={!periodSwitchAllowed}
              />
              <CheckoutCardPaidPlan
                selectedPack={selectedPack}
                selectedVolume={selectedVolume}
                currentPack={currentPack}
                couponCode={couponCode}
                monthlyInvoiceAmounts={monthlyInvoiceAmounts}
                invoiceAmounts={invoiceAmounts}
                isMonthly={isMonthly}
                availablePeriods={availablePeriods}
                isLoading={isLoading}
                onCouponCode={(couponCode) => setCouponCode(couponCode)}
                onItemSelected={handleOnItemSelected}
                periodSwitchAllowed={periodSwitchAllowed}
              />
            </>
          )}

          <div className="w-full flex flex-col space-y-4">
            <LoadingButton
              variant="contained"
              loading={checkoutIsLoading}
              color="primary"
              disabled={!formData.isValid}
              onClick={() => onCheckout(isFreePlan)}
            >
              {isFreePlan
                ? selectedPack.id === currentPack
                  ? 'Already subscribed to this plan'
                  : 'Place order'
                : step === CHECKOUT_STEPS.PAYMENT_METHOD
                ? 'Place order'
                : 'Proceed to checkout'}
            </LoadingButton>
          </div>
        </div>
      </div>
      <h3 className="font-medium text-dark mt-4 text-center">
        Any questions? Contact Sales - (650) 480 1760
      </h3>
    </>
  );
};

const CheckoutCardPaidPlan = ({
  selectedPack,
  selectedVolume,
  couponCode,
  currentPack,
  monthlyInvoiceAmounts,
  invoiceAmounts,
  isMonthly,
  availablePeriods,
  isLoading,
  onCouponCode,
  onItemSelected,
  periodSwitchAllowed,
}) => {
  const displayBilledToday =
    [BUTTON_ACTIONS.CHANGE_VOLUME, BUTTON_ACTIONS.UPGRADE].includes(selectedPack.buttonText) &&
    !['starter', 'pioneer', 'trial_end'].includes(currentPack);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col space-y-4">
        <div className="flex flex-col space-y-2  text-dark">
          <h4 className="font-bold text-dark">
            {selectedPack.displayName} + {formatNumber(selectedVolume)} containers a year:
          </h4>
          {displayBilledToday ? (
            <div className="flex flex-col space-y-2">
              <h2 className="text-dark font-bold">
                <LoadingComponent isLoading={isLoading}>
                  {formatCurrencyNumber(invoiceAmounts.amountDueNextMonth)}
                </LoadingComponent>{' '}
                per {isMonthly ? 'month' : 'year'}
              </h2>
              <h4 className="text-blue-900 font-medium">
                <LoadingComponent isLoading={isLoading}>
                  {formatCurrencyNumber(invoiceAmounts.amountDueNow)}
                </LoadingComponent>{' '}
                billed today
              </h4>
            </div>
          ) : (
            <div className="flex items-center">
              <LoadingComponent isLoading={isLoading}>
                {couponCode && (
                  <h2 className="line-through font-light mr-1">
                    {formatCurrencyNumber(invoiceAmounts.noCouponAmountDueNow)}
                  </h2>
                )}
                <h2 className="text-dark font-bold">
                  {formatCurrencyNumber(invoiceAmounts.amountDueNow)} per{' '}
                  {isMonthly ? 'month' : 'year'}
                </h2>
              </LoadingComponent>
            </div>
          )}
          {monthlyInvoiceAmounts && !isMonthly && (
            <h4 className="font-regular text-gray-700">
              Total savings with yearly:{' '}
              <span className="font-medium text-orange-900">
                {formatCurrencyNumber(
                  monthlyInvoiceAmounts.amountDueNextMonth * 12 - invoiceAmounts.amountDueNextMonth
                )}
              </span>
            </h4>
          )}
          {periodSwitchAllowed &&
            isMonthly &&
            availablePeriods.some((e) => e.value === 'yearly') && (
              <div
                className="text-orange-900 font-medium"
                onClick={() => onItemSelected(null, null, 'yearly')}
              >
                <h4 className="cursor-pointer">Pay upfront and save 10%</h4>
              </div>
            )}
        </div>
        <hr className="w-full bg-blue-200 border-none my-2 rounded-md" style={{ height: '1px' }} />
        <h4 className="font-bold text-dark">Bill Summary</h4>
        <div className="flex flex-col space-y-1">
          <h4 className="font-medium text-gray-800">12 months commitment</h4>
          <h4 className="italic text-gray-800">Adjust your plan within the first month</h4>
        </div>
      </div>
      <div className="mt-8">
        <CouponInput selectedPack={selectedPack} onCouponCode={(v) => onCouponCode(v)} />
        {displayBilledToday && <HowIsMyBillCalculated />}
      </div>
    </div>
  );
};

const HowIsMyBillCalculated = () => {
  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={false}
      sx={[
        () => ({
          '&:before': {
            backgroundColor: 'white',
          },
          background: 'none',
        }),
      ]}
    >
      <AccordionSummary
        // expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
        sx={{
          margin: 0,
          padding: 0,
          minHeight: '0px',
          '& .MuiAccordionSummary-content': {
            margin: '0px',
          },
        }}
      >
        <div className="flex space-x-1 items-center mb-2">
          <h4 className="text-gray-700 font-regular text-center ">How is my bill calculated?</h4>
          <ExpandMoreIcon style={{ fontSize: '16px' }} className="text-gray-800" />
        </div>
      </AccordionSummary>
      <AccordionDetails sx={{ margin: 0, padding: 0 }}>
        <h4 className="font-regular text-dark">
          Your last payment has been deducted from your next bill.
        </h4>
      </AccordionDetails>
    </Accordion>
  );
};

const CheckoutCardFreePlan = ({ selectedPack, selectedVolume }) => {
  return (
    <div className="flex flex-col space-y-4">
      <h4 className="font-bold text-gray-700">Bill Summary</h4>
      <div className="flex flex-col space-y-1">
        <h4 className="font-medium text-dark">
          {selectedPack.displayName} plan license + {formatNumber(selectedVolume)} complimentary
          container transactions:
        </h4>
        <h4 className="font-bold text-gray-800">Free</h4>
      </div>
      <hr className="w-full bg-blue-200 border-none my-4 rounded-md" style={{ height: '1px' }} />
      <div className="flex flex-col space-y-1 font-bold text-dark">
        <h4>Total</h4>
        <h2>Free</h2>
      </div>
    </div>
  );
};

export default CheckoutCard;
